// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { getUserFacingErrorMessage, handleAPIError } from '@/api/error-utils';
import { VoidInvoiceInput, voidInvoice } from '@/billing/api/void-invoice';
import { invalidateInvoiceQueries } from '@/billing/hooks/invalidate-invoice-queries';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

// Codes the caller handles explicitly (idempotent success, known status
// errors routed to the known-error snackbar). Skip Sentry — these are
// user-facing, not unexpected failures.
const EXPECTED_ERROR_CODES = new Set<number>([
  ERROR_CODE_MAPPING.invoice_already_void,
  ERROR_CODE_MAPPING.invoice_not_voidable,
  ERROR_CODE_MAPPING.invoice_has_settlements,
  ERROR_CODE_MAPPING.invoice_void_reason_required,
]);

interface UseVoidInvoiceOptions {
  invoiceKey: string;
  onSuccess?: () => void;
  onError?: (
    message: string,
    apiStatusCode: number,
    httpStatusCode: number
  ) => void;
}

export function useVoidInvoice(options: UseVoidInvoiceOptions) {
  const queryClient = useQueryClient();

  const invalidateAll = () =>
    invalidateInvoiceQueries({
      queryClient,
      invoiceKey: options.invoiceKey,
      includeCharges: true,
    });

  return useMutation({
    mutationFn: (input: VoidInvoiceInput) => voidInvoice(input),
    onSuccess: async () => {
      await invalidateAll();
      options.onSuccess?.();
    },
    onError: async (error: AxiosError) => {
      const apiError = handleAPIError(error);
      // Invalidate even on error — server state may have changed
      await invalidateAll();
      if (
        !apiError.sentToSentry &&
        !EXPECTED_ERROR_CODES.has(apiError.apiStatusCode)
      ) {
        Sentry.captureException(error);
      }
      options.onError?.(
        getUserFacingErrorMessage(apiError),
        apiError.apiStatusCode,
        apiError.httpStatusCode
      );
    },
  });
}
